'use client';

import { useEffect, useState } from 'react';
import { retrieveRawInitData } from '@telegram-apps/sdk-react';

interface InitDataSnapshot {
  data: string | undefined;
  ready: boolean;
}

const INITIAL_SNAPSHOT: InitDataSnapshot = { data: undefined, ready: false };

function useRawInitDataSnapshot(): InitDataSnapshot {
  const [snapshot, setSnapshot] = useState<InitDataSnapshot>(INITIAL_SNAPSHOT);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const devInitData = process.env.NEXT_PUBLIC_DEV_INIT_DATA;
      if (devInitData) {
        setSnapshot({ data: devInitData, ready: true });
        return;
      }
    }

    if (typeof window === 'undefined') {
      setSnapshot({ data: undefined, ready: true });
      return;
    }

    let resolved: string | undefined;

    try {
      const sdkInitData = retrieveRawInitData();
      if (sdkInitData) resolved = sdkInitData;
    } catch {
    }

    if (!resolved) {
      const tgInitData = window.Telegram?.WebApp?.initData;
      if (tgInitData) resolved = tgInitData;
    }

    if (!resolved) {
      const hash = window.location.hash || '';
      if (hash.includes('tgWebAppData=')) {
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const initData = params.get('tgWebAppData');
        if (initData) resolved = decodeURIComponent(initData);
      }
    }

    setSnapshot({ data: resolved, ready: true });
  }, []);

  return snapshot;
}

export function useRawInitDataWithDev(): string | undefined {
  return useRawInitDataSnapshot().data;
}

export function useRawInitDataReady(): boolean {
  return useRawInitDataSnapshot().ready;
}

export function parseInitData(initData: string | undefined) {
  if (!initData) return null;

  try {
    const params = new URLSearchParams(initData);
    const userParam = params.get('user');

    if (userParam) {
      return JSON.parse(decodeURIComponent(userParam));
    }
  } catch (error) {
    console.error('Failed to parse initData:', error);
  }

  return null;
}

export function useInitDataUser() {
  const initData = useRawInitDataWithDev();
  return parseInitData(initData);
}

export function useIsPremium(): boolean {
  const user = useInitDataUser();
  const isPremium = user?.is_premium === true;

  const isDev = process.env.NODE_ENV !== 'production';
  if (isDev && !user?.is_premium) {
    return true;
  }

  return isPremium;
}
